import { config } from '../config';
const currentVersion = '1.0';

/********************************************************************************
 **********************      Wallet Requests      *******************************
 *******************************************************************************/

// all wallet creation needs to be wallet 2.0 from this point onwards
export function createWallet({ walletPasscode, walletMnemonicRecoveryPhrase }) {
  return {
    method: 'POST',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets?core=true`,
    data: {
      mnemonic: walletMnemonicRecoveryPhrase,
      passcode: walletPasscode,
    },
  };
}

export function getUserWalletItem({ symbol, isLegacy }) {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/${symbol}${
      isLegacy ? '' : '?core=true'
    }`,
  };
}

export function getUserAddresses(isLegacy) {
  return {
    method: 'GET',
    // Use non-versioned route here because some prod gateways only expose /api/wallets/*
    // for address endpoints even when v1 routes exist in service code.
    url: `${config.core.walletServiceUrl}/api/wallets/${isLegacy ? 'legacy-addresses' : 'addresses'}`,
  };
}

export function getUserLegacyAddresses() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/api/wallets/legacy-addresses`,
  };
}

export function importWallet({
  walletMnemonicRecoveryPhrase,
  walletPasscode,
  hasLegacyWallet,
}) {
  return {
    method: 'POST',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/import${
      hasLegacyWallet ? '?forgotMnemonic=true' : ''
    }`,
    data: {
      mnemonic: walletMnemonicRecoveryPhrase,
      passcode: walletPasscode,
    },
  };
}

// Legacy-wallet upgrade to Wallet 2.0. The backend validates that the mnemonic
// derives to the member's migrated legacy ETH address before creating the wallet,
// so a wrong phrase is rejected (a real "invalid mnemonic") instead of silently
// creating a new wallet at a different address. forgotMnemonic=true skips the
// check (the "forgot recovery phrase" path — member accepts a brand-new wallet).
export function upgradeWallet({
  walletMnemonicRecoveryPhrase,
  walletPasscode,
  forgotMnemonic,
}) {
  return {
    method: 'POST',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/upgrade${
      forgotMnemonic ? '?forgotMnemonic=true' : ''
    }`,
    data: {
      mnemonic: walletMnemonicRecoveryPhrase,
      passcode: walletPasscode,
    },
  };
}

export function getWalletMnemonicRecoveryPhrase() {
  return {
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/mnemonic`,
  };
}

export function changeWalletPasscode({ mnemonic, newPasscode, core }) {
  return {
    method: 'PUT',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/passcode${
      core ? '?core=true' : ''
    }`,
    data: {
      mnemonic,
      newPasscode,
    },
  };
}

/********************************************************************************
 ********************      Transaction Requests      ****************************
 *******************************************************************************/
export function getTransactionHistory({ symbol, page, pageSize, sortBy, sortDir, core }) {
  if (sortBy === 'date') sortBy = 'timestamp';
  const sortByParam = `${sortBy}+${sortDir}`;

  const queryParams = `?page=${page}&pageSize=${pageSize}&sortBy=${sortByParam}${
    core ? '&core=true' : ''
  }`;

  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/transactions/${symbol}${queryParams}`,
  };
}

export function sendTransaction({ amount, symbol, to, walletPassword, core, gasFee }) {
  return {
    method: 'POST',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/transactions${
      core ? '?core=true' : ''
    }`,
    data: { amount, symbol, to, walletPassword, gasFee },
  };
}

export function sendCoreBridgeTransaction({
  amountToBridge,
  walletPassword,
  tankAmountEth,
  tankTransactionFeeEth,
}) {
  const bridgeVersion = '2.0';

  return {
    method: 'POST',
    url: `${config.core.walletServiceUrl}/v${bridgeVersion}/layer2/bridge`,
    data: {
      amountToBridge,
      walletPassword,
      tankAmountEth,
      tankTransactionFeeEth,
    },
  };
}

export function sendL2Transaction({ amount, to, walletPassword, gasFee, core }) {
  return {
    method: 'POST',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/layer2/send${
      core ? '?core=true' : ''
    }`,
    data: { amount, to, walletPassword, gasFee },
  };
}

/********************************************************************************
 **********************     Gas Fee Requests     ********************************
 *******************************************************************************/

export function getL2BridgeGasFee({ amount, core = false }) {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/layer2/bridge-gas-fee?core=${core}&amount=${amount}`,
  };
}

export function getL2TransferGasFee({ amount = 0, to = '', core = false }) {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/layer2/transfer-gas-fee?core=${core}&to=${to}&amount=${amount}`,
  };
}

export function getGasFee({ amount, to, symbol = 'ETH', core = false }) {
  let url = `${config.core.walletServiceUrl}/v${currentVersion}/transactions/fee/${symbol}?core=${core}`;
  if (amount && to) {
    url += `&amount=${amount}&to=${to}`;
  }
  return {
    url,
    method: 'GET',
  };
}

export function getL2BridgeGasEthFee() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/layer2/eth-gas-tank`,
  };
}

/** ****** Chain Info ********/
export function getChainInfo() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/api/chainInfo`,
  };
}

/********************************************************************************
 *********************     Private Token Requests     **************************
 *******************************************************************************/

export function getL2ChainInfo() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/api/chainInfo/layer2`,
  };
}

export function getPrivateTokenTransactionHistory({
  url,
  page,
  pageSize,
  sortBy, // eslint-disable-line @typescript-eslint/no-unused-vars
  sortDir,
}) {
  // ElevateScan uses Etherscan-style API format:
  // - page: page number (1-indexed in Etherscan, but we use 0-indexed)
  // - offset: number of results per page
  // - sort: 'desc' or 'asc' (lowercase)
  // Note: Etherscan uses 1-indexed pages, but we'll use 0-indexed and add 1
  // Note: sortBy parameter is kept for API compatibility but not used by Etherscan API
  const etherscanPage = page + 1; // Convert 0-indexed to 1-indexed
  const sort = sortDir?.toLowerCase() === 'desc' ? 'desc' : 'asc';

  // If URL already has query params (from NativeTransactionHistory), append with &
  const separator = url.includes('?') ? '&' : '?';
  const queryParams = `${separator}page=${etherscanPage}&offset=${pageSize}&sort=${sort}`;

  return {
    method: 'GET',
    url: `${url}${queryParams}`,
  };
}

/********************************************************************************
 *********************     Bridge Requests     **************************
 *******************************************************************************/

export function getBridgeConfigurations() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/bridge-x-configurations`,
  };
}

/********************************************************************************
 **********************     Coin Conversion     ********************************
 *******************************************************************************/

export function getCoinConversion({ symbol, currency }) {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/cryptoPrice?symbol=${symbol}&currency=${currency}`,
  };
}

export function getTokenConversions({ symbols, currency }) {
  const symbolParams = symbols.map((symbol) => `symbol=${symbol}`).join('&');

  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/transactions/getPriceMultiQuote?currency=${currency}&${symbolParams}`,
  };
}

/********************************************************************************
 **********************     BTC Testnet Wallet Migration     ********************************
 *******************************************************************************/

export function btcMigrationRequired() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/api/wallets/is-btc-migration-required`,
  };
}
export function getMemberSalts() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/api/wallets/encrypted-mnemonic`,
  };
}

export function getLegacySalts(from) {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/legacy/${from}/key`,
  };
}

export function updateBtcAddress({ btcAddress }) {
  return {
    method: 'PUT',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/wallets/btc-address`,
    data: {
      btcAddress,
    },
  };
}

export function completeBtcMigration() {
  return {
    method: 'POST',
    url: `${config.core.walletServiceUrl}/api/wallets/complete-btc-migration`,
  };
}

export function getCryptoAssets() {
  return {
    method: 'GET',
    url: `${config.core.walletServiceUrl}/v${currentVersion}/crypto-asset`,
  };
}

export function getBepTxnHistory() {}
